import React, { useState } from "react";
import {
  Check,
  Play,
  Smartphone,
  Zap,
  Clock,
  ChevronDown,
  ChevronUp,
  X,
  CreditCard,
  RefreshCw,
  ArrowLeft,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { useRemoteConfigString } from "@/hooks/useRemoteConfig";

// --- Constants ---
// Default number if remote config fails
const DEFAULT_NUMBER = "972507062070";
const PRIMARY_CTA_COLOR = "bg-[#25D366] hover:bg-[#20bd5a] text-white"; // WhatsApp Green

// --- Components ---

const Section = ({
  children,
  className = "",
  id = "",
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => (
  <section
    id={id}
    className={`py-16 px-4 md:px-8 max-w-7xl mx-auto ${className}`}
  >
    {children}
  </section>
);

const AccordionItem = ({
  question,
  answer,
  isOpen,
  onClick,
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}) => (
  <div className="border-b border-gray-200 last:border-0">
    <button
      className="w-full flex justify-between items-center py-4 text-right font-medium text-gray-900 focus:outline-none"
      onClick={onClick}
    >
      <span>{question}</span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-gray-500" />
      ) : (
        <ChevronDown className="w-5 h-5 text-gray-500" />
      )}
    </button>
    <div
      className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? "max-h-96 opacity-100 pb-4" : "max-h-0 opacity-0"}`}
    >
      <p className="text-gray-600">{answer}</p>
    </div>
  </div>
);

export default function LandingPageV2() {
  const { t } = useTranslation();
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  // Use the custom hook to fetch the phone number, falling back to DEFAULT_NUMBER
  const whatsappNumber = useRemoteConfigString(
    "whatsapp_sales_number",
    DEFAULT_NUMBER
  );

  const WHATSAPP_MESSAGE = t("landingV2.whatsappMessage");
  const WHATSAPP_URL = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const benefitsList = t("landingV2.benefits.list", { returnObjects: true });
  const benefits = Array.isArray(benefitsList) ? benefitsList : [];

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900" dir="rtl">
      {/* --- Sticky CTA Mobile --- */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/90 backdrop-blur-md border-t border-gray-200 md:hidden z-50 flex justify-center shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={`w-full max-w-sm flex items-center justify-center gap-2 py-3 px-6 rounded-full font-bold text-lg shadow-lg ${PRIMARY_CTA_COLOR} transition-transform active:scale-95`}
        >
          <Smartphone className="w-5 h-5" />
          {t("landingV2.hero.primaryCta")}
        </a>
      </div>

      {/* --- Navbar --- */}
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-bold text-xl tracking-tight text-slate-900">
            <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center text-white">
              <Zap className="w-5 h-5" />
            </div>
            SmartMenu
          </div>
          <div className="hidden md:flex gap-6 items-center">
            <button
              onClick={() => scrollToSection("how-it-works")}
              className="text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              {t("landingV2.howItWorks.title")}
            </button>
            <button
              onClick={() => scrollToSection("benefits")}
              className="text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              {t("landingV2.benefits.title")}
            </button>
            <button
              onClick={() => scrollToSection("faq")}
              className="text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              {t("landingV2.faq.title")}
            </button>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`px-5 py-2 rounded-full font-semibold text-sm transition-all hover:shadow-lg ${PRIMARY_CTA_COLOR}`}
            >
              {t("landingV2.hero.primaryCta")}
            </a>
          </div>
        </div>
      </nav>

      {/* --- Section 1: Hero --- */}
      <Section className="pt-20 pb-20 md:pb-32 grid md:grid-cols-2 gap-12 items-center">
        <div className="space-y-6 text-center md:text-right">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold uppercase tracking-wide">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
            {t("landingV2.hero.badge")}
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.15] tracking-tight">
            {t("landingV2.hero.titlePart1")}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600">
              {t("landingV2.hero.titlePart2")}
            </span>
            .
          </h1>

          <p className="text-lg md:text-xl text-slate-600 max-w-lg mx-auto md:mx-0 leading-relaxed">
            {t("landingV2.hero.description")}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start pt-2">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all ${PRIMARY_CTA_COLOR}`}
            >
              <Smartphone className="w-5 h-5" />
              {t("landingV2.hero.primaryCta")}
            </a>
            <button
              onClick={() => setIsVideoOpen(true)}
              className="flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold text-lg bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm"
            >
              <Play className="w-5 h-5 fill-current" />
              {t("landingV2.hero.secondaryCta")}
            </button>
          </div>

          <p className="text-sm text-slate-500 pt-2 flex items-center justify-center md:justify-start gap-2">
            <Check className="w-4 h-4 text-green-500" />{" "}
            {t("landingV2.hero.trust")}
          </p>
        </div>

        <div className="relative mx-auto w-full max-w-[320px] md:max-w-[380px]">
          {/* Phone Frame */}
          <div className="relative rounded-[2.5rem] overflow-hidden border-[8px] border-slate-900 bg-slate-900 shadow-2xl z-10">
            <div className="aspect-[9/19] bg-white flex flex-col relative">
              {/* Mock Header */}
              <div
                className="absolute top-0 inset-x-0 h-24 bg-cover bg-center"
                style={{
                  backgroundImage:
                    'url("https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&q=80&w=600")',
                }}
              >
                <div className="absolute inset-0 bg-black/40"></div>
                <div className="absolute bottom-4 right-4 text-white font-bold text-xl">
                  Burst Burger
                </div>
              </div>

              {/* Mock Content */}
              <div className="mt-24 p-4 space-y-4 overflow-hidden">
                <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
                  <div className="bg-slate-900 text-white px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                    המומלצים
                  </div>
                  <div className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                    עיקריות
                  </div>
                  <div className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                    שתייה
                  </div>
                </div>

                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="flex gap-3 items-start border-b border-gray-100 pb-4 last:border-0"
                  >
                    <div
                      className="w-20 h-20 rounded-lg bg-gray-100 bg-cover bg-center shrink-0"
                      style={{
                        backgroundImage: `url("https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&q=80&w=200&ixlib=rb-4.0.3")`,
                      }}
                    ></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start">
                        <h4 className="font-bold text-sm text-slate-900 truncate">
                          צ'יזבורגר קלאסי
                        </h4>
                        <span className="text-slate-900 font-bold text-sm">
                          ₪55
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                        קציצת בקר עסיסית 200 גרם, גבינת צ'דר, חסה, עגבניה או
                        רוטב הבית.
                      </p>
                      <button className="mt-2 text-xs font-bold text-green-600 flex items-center gap-1">
                        הוסף להזמנה{" "}
                        <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center">
                          +
                        </div>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Phone Notch */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-900 rounded-b-2xl"></div>
          </div>

          {/* Decorative Elements */}
          <div className="absolute top-10 -left-10 w-24 h-24 bg-yellow-400 rounded-full blur-2xl opacity-20 animate-pulse"></div>
          <div className="absolute bottom-10 -right-10 w-32 h-32 bg-green-500 rounded-full blur-3xl opacity-20"></div>
        </div>
      </Section>

      {/* --- Section 2: Problem --- */}
      <section className="py-16 bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              {t("landingV2.problem.title")}
            </h2>
            <p className="text-slate-600">{t("landingV2.problem.subtitle")}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: CreditCard,
                title: t("landingV2.problem.cards.expensive.title"),
                desc: t("landingV2.problem.cards.expensive.desc"),
              },
              {
                icon: RefreshCw,
                title: t("landingV2.problem.cards.hardUpdate.title"),
                desc: t("landingV2.problem.cards.hardUpdate.desc"),
              },
              {
                icon: Clock,
                title: t("landingV2.problem.cards.slowService.title"),
                desc: t("landingV2.problem.cards.slowService.desc"),
              },
            ].map((item, i) => (
              <Card key={i} className="border-none shadow-md bg-white">
                <CardContent className="pt-6 p-6 flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center text-red-500 mb-4">
                    <item.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {item.desc}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* --- Section 3: Solution / Benefits --- */}
      <Section
        id="benefits"
        className="grid md:grid-cols-2 gap-12 items-center"
      >
        <div className="order-2 md:order-1 relative">
          <img
            src="https://images.unsplash.com/photo-1556740738-b6a63e27c4df?auto=format&fit=crop&q=80&w=1000"
            alt="Customer scanning QR code"
            className="rounded-2xl shadow-2xl object-cover h-[500px] w-full"
          />
          <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl max-w-xs border border-slate-100 hidden md:block">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold text-lg">
                98%
              </div>
              <div>
                <div className="font-bold text-slate-900">
                  {t("landingV2.benefits.satisfaction")}
                </div>
                <div className="text-xs text-slate-500">
                  {t("landingV2.benefits.satisfactionDesc")}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="order-1 md:order-2 space-y-8">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t("landingV2.benefits.title")}
            </h2>
            <p className="text-lg text-slate-600">
              {t("landingV2.benefits.description")}
            </p>
          </div>

          <div className="space-y-4">
            {benefits.map((benefit: string, i: number) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
                <span className="text-slate-800 font-medium">{benefit}</span>
              </div>
            ))}
          </div>

          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center font-bold text-green-700 hover:text-green-800 border-b-2 border-green-200 hover:border-green-600 transition-all pb-1 group"
          >
            {t("landingV2.benefits.cta")}{" "}
            <ArrowLeft className="mr-2 w-4 h-4 transition-transform group-hover:-translate-x-1" />
          </a>
        </div>
      </Section>

      {/* --- Section 4: How It Works --- */}
      <section id="how-it-works" className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t("landingV2.howItWorks.title")}
            </h2>
            <p className="text-slate-400">
              {t("landingV2.howItWorks.subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: t("landingV2.howItWorks.steps.1.title"),
                desc: t("landingV2.howItWorks.steps.1.desc"),
              },
              {
                step: "02",
                title: t("landingV2.howItWorks.steps.2.title"),
                desc: t("landingV2.howItWorks.steps.2.desc"),
              },
              {
                step: "03",
                title: t("landingV2.howItWorks.steps.3.title"),
                desc: t("landingV2.howItWorks.steps.3.desc"),
              },
            ].map((item, i) => (
              <div
                key={i}
                className="relative p-8 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
              >
                <div className="absolute -top-6 -right-6 text-6xl font-black text-white/5 select-none font-serif">
                  {item.step}
                </div>
                <div className="relative z-10">
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- Section 5: Example Menu Video --- */}
      <Section className="text-center py-20">
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">
          {t("landingV2.video.title")}
        </h2>

        <div
          className="relative w-full max-w-4xl mx-auto aspect-video bg-slate-900 rounded-2xl shadow-2xl overflow-hidden group cursor-pointer"
          onClick={() => setIsVideoOpen(true)}
        >
          {/* Placeholder for Video Overlay */}
          <img
            src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&q=80&w=1200"
            alt="Video Thumbnail"
            className="w-full h-full object-cover opacity-60 group-hover:opacity-40 transition-opacity"
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <Play className="w-8 h-8 text-green-600 fill-current ml-1" />
            </div>
          </div>

          <div className="absolute bottom-6 right-6 text-white text-right hidden sm:block">
            <div className="font-bold text-lg">
              {t("landingV2.video.label")}
            </div>
            <div className="text-sm text-slate-300">1 דקה 20 שניות</div>
          </div>
        </div>

        <div className="mt-8">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-all ${PRIMARY_CTA_COLOR}`}
          >
            <Smartphone className="w-5 h-5" />
            {t("landingV2.hero.primaryCta")}
          </a>
        </div>
      </Section>

      {/* --- Section 6: Social Proof --- */}
      <section className="py-16 bg-green-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="mb-6 flex justify-center text-green-500">
            {[1, 2, 3, 4, 5].map((i) => (
              <Zap key={i} className="w-6 h-6 fill-current" />
            ))}
          </div>
          <h3 className="text-2xl font-bold text-slate-900 mb-4">
            "{t("landingV2.socialProof.quote")}"
          </h3>
          <p className="text-lg text-slate-600 italic mb-8">
            "{t("landingV2.socialProof.text")}"
          </p>
          <div>
            <div className="font-bold text-slate-900">
              {t("landingV2.socialProof.author")}
            </div>
            <div className="text-sm text-slate-500">
              {t("landingV2.socialProof.role")}
            </div>
          </div>
        </div>
      </section>

      {/* --- Section 7: Final CTA --- */}
      <section className="py-24 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6">
            {t("landingV2.finalCta.title")}
          </h2>
          <p className="text-xl text-slate-600 mb-10">
            {t("landingV2.finalCta.subtitle")}
          </p>

          <div className="flex flex-col items-center gap-4">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`w-full sm:w-auto px-10 py-5 rounded-full font-bold text-xl shadow-xl hover:shadow-2xl hover:scale-105 transition-all flex items-center justify-center gap-3 ${PRIMARY_CTA_COLOR}`}
            >
              <Smartphone className="w-6 h-6" />
              {t("landingV2.finalCta.button")}
            </a>
            <div className="text-sm text-slate-500 font-medium">
              {t("landingV2.finalCta.note")}
            </div>
          </div>
        </div>
      </section>

      {/* --- Section 8: FAQ --- */}
      <Section id="faq" className="max-w-3xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-900">
            {t("landingV2.faq.title")}
          </h2>
        </div>

        <div className="space-y-2">
          {[
            {
              q: t("landingV2.faq.items.app.q"),
              a: t("landingV2.faq.items.app.a"),
            },
            {
              q: t("landingV2.faq.items.update.q"),
              a: t("landingV2.faq.items.update.a"),
            },
            {
              q: t("landingV2.faq.items.phones.q"),
              a: t("landingV2.faq.items.phones.a"),
            },
            {
              q: t("landingV2.faq.items.setup.q"),
              a: t("landingV2.faq.items.setup.a"),
            },
          ].map((item, i) => (
            <AccordionItem
              key={i}
              question={item.q}
              answer={item.a}
              isOpen={openFaqIndex === i}
              onClick={() => toggleFaq(i)}
            />
          ))}
        </div>
      </Section>

      {/* --- Footer --- */}
      <footer className="bg-slate-50 pt-16 pb-8 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2">
              <div className="flex items-center gap-2 font-bold text-xl text-slate-900 mb-4">
                <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center text-white">
                  <Zap className="w-5 h-5" />
                </div>
                SmartMenu
              </div>
              <p className="text-slate-500 max-w-sm">
                {t("landingV2.footer.desc")}
              </p>
            </div>

            <div>
              <h4 className="font-bold text-slate-900 mb-4">
                {t("landingV2.footer.contact")}
              </h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>
                  <a
                    href={WHATSAPP_URL}
                    className="hover:text-green-600 transition-colors"
                  >
                    {t("landingV2.footer.whatsappSupport")}
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:hello@smartmenu.com"
                    className="hover:text-green-600 transition-colors"
                  >
                    hello@smartmenu.com
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-slate-900 mb-4">
                {t("landingV2.footer.legal")}
              </h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>
                  <a href="#" className="hover:text-slate-900">
                    {t("landingV2.footer.privacy")}
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-slate-900">
                    {t("landingV2.footer.terms")}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-400">
            <div>
              &copy; {new Date().getFullYear()} {t("landingV2.footer.rights")}
            </div>
            <div className="flex gap-4">{/* Social Icons Placeholder */}</div>
          </div>
        </div>
      </footer>

      {/* --- Video Modal Overlay --- */}
      {isVideoOpen && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="relative w-full max-w-5xl aspect-video bg-black rounded-xl overflow-hidden shadow-2xl">
            <button
              onClick={() => setIsVideoOpen(false)}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="w-full h-full flex items-center justify-center text-white">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1"
                title="Example Menu Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
